/**
 * Hook to observe the YDoc execution plan reactively.
 *
 * The execution plan is a top-level Y.Map keyed by node_id, where each
 * value is a Y.Map with { type, label, sparks, batch_id, status, action? }.
 *
 * Written by the orchestrator when cost confirmation is needed.
 * The frontend observes this map to show execution plan cards.
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import * as Y from 'yjs'
import { apiFetch, InsufficientSparksError, createDebug, type YDocValue } from '@ponpon/api-client'
const log = createDebug('dw:exec-plan:observe')

export interface ExecutionPlanEntry {
  nodeId: string
  type: string
  action?: string
  label: string
  sparks: number
  batchId: string
  status: 'pending' | 'confirmed' | 'cancelled'
}

export interface ExecutionPlanBatch {
  batchId: string
  entries: ExecutionPlanEntry[]
  totalSparks: number
  status: 'pending' | 'confirmed' | 'cancelled'
}

export interface UseExecutionPlanResult {
  /** All batches in the execution plan, grouped by batch_id */
  batches: ExecutionPlanBatch[]
  /** Whether there are any pending batches */
  hasPendingPlan: boolean
  /** Confirm a specific batch */
  confirmBatch: (batchId: string) => Promise<void>
  /** Cancel a specific batch */
  cancelBatch: (batchId: string) => Promise<void>
}

type PlanEntryMap = Y.Map<YDocValue>
type PlanMap = Y.Map<PlanEntryMap>
type ExecutionPlanStatus = ExecutionPlanEntry['status']

function yString(map: PlanEntryMap, key: string, fallback: string): string {
  const value = map.get(key)
  return typeof value === 'string' ? value : fallback
}

function yNumber(map: PlanEntryMap, key: string, fallback: number): number {
  const value = map.get(key)
  return typeof value === 'number' ? value : fallback
}

function yPlanStatus(map: PlanEntryMap, key: string, fallback: ExecutionPlanStatus): ExecutionPlanStatus {
  const value = map.get(key)
  return value === 'pending' || value === 'confirmed' || value === 'cancelled' ? value : fallback
}

function readPlanEntries(planMap: PlanMap): ExecutionPlanEntry[] {
  const entries: ExecutionPlanEntry[] = []
  planMap.forEach((entryMap, nodeId) => {
    if (entryMap instanceof Y.Map) {
      entries.push({
        nodeId,
        type: yString(entryMap, 'type', ''),
        action: yString(entryMap, 'action', '') || undefined,
        label: yString(entryMap, 'label', nodeId),
        sparks: yNumber(entryMap, 'sparks', 0),
        batchId: yString(entryMap, 'batch_id', ''),
        status: yPlanStatus(entryMap, 'status', 'pending'),
      })
    }
  })
  return entries
}

function groupByBatch(entries: ExecutionPlanEntry[]): ExecutionPlanBatch[] {
  const batchMap = new Map<string, ExecutionPlanEntry[]>()
  for (const entry of entries) {
    const key = entry.batchId || '__default__'
    if (!batchMap.has(key)) batchMap.set(key, [])
    batchMap.get(key)!.push(entry)
  }
  const batches: ExecutionPlanBatch[] = []
  for (const [batchId, batchEntries] of batchMap) {
    const totalSparks = batchEntries.reduce((sum, e) => sum + e.sparks, 0)
    const status = batchEntries[0]?.status ?? 'pending'
    batches.push({ batchId, entries: batchEntries, totalSparks, status })
  }
  // Sort by batch number so nodes appear in chronological order.
  // Batch IDs are "batch-1", "batch-2", etc.
  batches.sort((a, b) => {
    const na = parseInt(a.batchId.split('-').pop() ?? '0', 10) || 0
    const nb = parseInt(b.batchId.split('-').pop() ?? '0', 10) || 0
    return na - nb
  })
  return batches
}

export function useExecutionPlan(
  ydoc: Y.Doc | null,
  projectId: string,
): UseExecutionPlanResult {
  const [entries, setEntries] = useState<ExecutionPlanEntry[]>([])

  useEffect(() => {
    if (!ydoc) return

    const planMap = ydoc.getMap<PlanEntryMap>('execution_plan')

    // Initial read
    setEntries(readPlanEntries(planMap))

    // Observe changes
    const observer = () => {
      const newEntries = readPlanEntries(planMap)
      if (import.meta.env.DEV) {
        log('observeDeep fired, entries: %d', newEntries.length)
      }
      setEntries(newEntries)
    }
    planMap.observeDeep(observer)

    return () => {
      planMap.unobserveDeep(observer)
    }
  }, [ydoc])

  const batches = useMemo(() => groupByBatch(entries), [entries])

  const clearLocalBatch = useCallback(
    (batchId: string, targetStatus: 'pending_execution' | 'idle' = 'pending_execution') => {
      if (!ydoc) return
      const planMap = ydoc.getMap<PlanEntryMap>('execution_plan')
      const nodesMap = ydoc.getMap<PlanEntryMap>('nodes')
      const toDelete: string[] = []
      planMap.forEach((entry, nodeId) => {
        if (entry instanceof Y.Map && entry.get('batch_id') === batchId) {
          toDelete.push(nodeId)
        }
      })
      if (import.meta.env.DEV) {
        log('client-side clear: %o batchId=%s → %s', toDelete, batchId, targetStatus)
      }
      if (toDelete.length > 0) {
        ydoc.transact(() => {
          for (const nodeId of toDelete) {
            planMap.delete(nodeId)
            // Transition node status so the canvas doesn't stay stuck at
            // pending_cost_confirmation when the backend's exit_pcc() write
            // is dropped (e.g. REST routed to a different instance).
            const ynode = nodesMap.get(nodeId)
            if (ynode instanceof Y.Map && ynode.get('status') === 'pending_cost_confirmation') {
              ynode.set('status', targetStatus)
            }
          }
        })
      }
    },
    [ydoc],
  )

  const confirmBatch = useCallback(
    async (batchId: string) => {
      const res = await apiFetch(
        `/projects/${projectId}/agentic/execution-plans/${encodeURIComponent(batchId)}/confirm`,
        { method: 'POST' },
      )
      if (res.status === 402) {
        const body = await res.json().catch(() => null)
        const detail = body?.detail ?? body
        if (
          detail &&
          typeof detail === 'object' &&
          detail.code === 'insufficient_sparks' &&
          typeof detail.balance === 'number' &&
          typeof detail.required === 'number'
        ) {
          throw new InsufficientSparksError(
            detail.balance,
            detail.required,
            detail.message ?? 'Insufficient credits',
          )
        }
      }
      if (res.status === 410) {
        clearLocalBatch(batchId, 'pending_execution')
        return
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.detail?.error ?? body?.detail ?? `Confirmation failed: ${res.status}`)
      }
      if (import.meta.env.DEV) {
        log('confirm REST succeeded for batch %s', batchId)
      }
      clearLocalBatch(batchId, 'pending_execution')
    },
    [clearLocalBatch, projectId],
  )

  const cancelBatch = useCallback(
    async (batchId: string) => {
      const res = await apiFetch(
        `/projects/${projectId}/agentic/execution-plans/${encodeURIComponent(batchId)}/cancel`,
        { method: 'POST' },
      )
      if (res.status === 410) {
        clearLocalBatch(batchId, 'idle')
        return
      }
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.detail?.error ?? body?.detail ?? `Cancel failed: ${res.status}`)
      }
      clearLocalBatch(batchId, 'idle')
    },
    [clearLocalBatch, projectId],
  )

  return {
    batches,
    hasPendingPlan: batches.some((b) => b.status === 'pending'),
    confirmBatch,
    cancelBatch,
  }
}
